import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { ContractFormValues } from '../../contract-form';
import type { ContractServiceConfigOption } from '@/api/contracts';
import { readProductConfigOptions } from '../product-config-option';
import { useServicePriceTotal } from '../use-service-price-total';
import { TemplatePanel } from '../template-panel';
import type { ProductTemplateProps } from '../template-props';
import { ServerVbmServerRow } from './server-vbm-server-row';
import { ServerVbmWarehouseRow } from './server-vbm-warehouse-row';

/**
 * Server.VBM template (also Device.Liquidation — same builder).
 * Reads `product_config_options` off the product detail, seeds
 * `service_configoptions` once (guarded so re-renders don't clobber user edits),
 * then renders per-option config rows.
 *
 * - `server-warehouse` type → ServerVbmServerRow (model/brand/tag/cores)
 * - `warehouse` type        → ServerVbmWarehouseRow (model/brand/qty/price/capacity, add/remove clone)
 */
export function ServerVbmTemplate({ index, templateName, detail }: ProductTemplateProps) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();
  const optsPath = `products.${index}.service_configoptions` as const;

  const options = useWatch({ control, name: optsPath }) as ContractServiceConfigOption[] | undefined;

  // Derive register price from Σ(qty × unit price) so the contract Total reflects
  // the configured devices (same commitment model as VCloud).
  useServicePriceTotal(index);

  // Seed defaults once from product_config_options; bail if already populated.
  useEffect(() => {
    const current = getValues(optsPath);
    if (Array.isArray(current) && current.length > 0) return;

    const productOpts = readProductConfigOptions(detail);
    if (productOpts.length === 0) return;

    const seeded: ContractServiceConfigOption[] = productOpts
      .map((o) => ({
        name: o.name,
        type: o.type,
        unit: o.unit,
        title: o.title,
        commitment_prepaid: true,
        statementable: false,
        commitment_value: o.value ?? 0,
        commitment_time: 0,
        commitment_price_per_unit: o.price ?? undefined,
        resizable: o.is_editable ?? false,
        price_config: { method: 'progressive' },
        prices: (o.product_pricing_steps ?? []).map((s) => ({
          price_start: s.price_start,
          price_end: s.price_end,
          price_per_unit: s.price_per_unit,
        })),
        // Warehouse scope — server-warehouse rows are default scope, warehouse rows are default
        scope: 'default',
        max: o.max ?? 999999999,
      }))
      // server-warehouse rows first, matching legacy sort
      .sort((a, b) => {
        if (a.type === 'server-warehouse' && b.type !== 'server-warehouse') return -1;
        if (b.type === 'server-warehouse' && a.type !== 'server-warehouse') return 1;
        return 0;
      });

    setValue(optsPath, seeded, { shouldDirty: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optsPath]);

  if (!Array.isArray(options) || options.length === 0) return null;

  /** Insert a clone of the row at `afterIndex` directly after it. */
  function handleInsert(afterIndex: number) {
    const current = (getValues(optsPath) as ContractServiceConfigOption[]) ?? [];
    const clone: ContractServiceConfigOption = { ...current[afterIndex], commitment_value: 0, scope: 'clone' };
    const next = [...current.slice(0, afterIndex + 1), clone, ...current.slice(afterIndex + 1)];
    setValue(optsPath, next, { shouldDirty: true });
  }

  /** Remove the row at `removeIndex` (only clone-scope rows may be removed). */
  function handleRemove(removeIndex: number) {
    const current = (getValues(optsPath) as ContractServiceConfigOption[]) ?? [];
    setValue(optsPath, current.filter((_, i) => i !== removeIndex), { shouldDirty: true });
  }

  const title = templateName === 'Device.Liquidation' ? 'Device configuration' : 'Server configuration';

  return (
    <TemplatePanel title={title}>
      {options.map((opt, i) => {
        const scope = (opt as Record<string, unknown>).scope as string | undefined;
        const isDefault = scope === 'default';

        if (opt.type === 'server-warehouse') {
          return (
            <ServerVbmServerRow
              key={`server-${i}`}
              optsPath={optsPath}
              rowIndex={i}
            />
          );
        }

        if (opt.type === 'warehouse') {
          return (
            <ServerVbmWarehouseRow
              key={`warehouse-${i}-${opt.name}`}
              optsPath={optsPath}
              rowIndex={i}
              isDefault={isDefault}
              onInsert={handleInsert}
              onRemove={handleRemove}
            />
          );
        }

        return null;
      })}
    </TemplatePanel>
  );
}
